import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle, Play, RefreshCw, Search, X, TreePine } from 'lucide-react';
import BlogContentActionModal from '../components/BlogContentActionModal';
import BlogCreatorWorkflowDetails from '../components/BlogCreatorWorkflowDetails';
import { API_ENDPOINTS } from '../config/api';
import { CITRUS_TREES } from '../data/citrusTreesData';
import { useBrand } from '../context/BrandContext';
import { BrandKey } from '../config/brands';
import { getBrandStorageItem, getBrandStorageKey } from '../utils/brandStorage';

type BestOfStatus = 'pending' | 'running' | 'completed' | 'error';
type BestOfBrandKey = Extract<BrandKey, 'uscitrusnursery' | 'drmanismagic'>;

interface BestOfRow {
  id: string;
  brandKey: BestOfBrandKey;
  keyword: string;
  itemName: string;
  location: string;
  articleTitle: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  competitorUrls: string[];
  contentLength: 'short' | 'medium' | 'long';
  category: string;
  status: BestOfStatus;
  progress: string;
  output: string;
  createdAt: Date;
  metadata?: {
    title: string;
    description: string;
    metaSeoTitle?: string;
    metaDescription?: string;
    word_count: number;
    seo_optimized: boolean;
    citations_included: boolean;
    brand_variables_processed: number;
    aeo_optimized: boolean;
  };
  generationResult?: any;
  workflowDetails?: {
    firecrawl?: any;
    deepResearch?: any;
    gapAnalysis?: any;
    contentGeneration?: any;
    currentStage?: string;
    retryCount?: number;
    canResume?: boolean;
  };
}

const DEFAULT_LOCATION = 'Texas';

const DR_MANI_DEFAULT_ROWS = [
  {
    id: 'plant-super-boost',
    itemName: "Dr. Mani's Magic Plant Super Boost",
    category: 'Microbes',
    primaryKeyword: 'best live microbes for plants',
    secondaryKeywords: ['beneficial microbes for soil', 'plant probiotics', 'live microbial inoculant'],
  },
  {
    id: 'crab-kelp-amino-acids',
    itemName: "Dr. Mani's Magic Crab, Kelp & Amino Acids",
    category: 'Fertilizer',
    primaryKeyword: 'best organic fertilizer for citrus trees',
    secondaryKeywords: ['salt free citrus fertilizer', 'crab kelp amino acids fertilizer', 'organic plant nutrition'],
  },
  {
    id: 'super-soil',
    itemName: "Dr. Mani's Magic Super Soil",
    category: 'Soil',
    primaryKeyword: 'best soil for container citrus trees',
    secondaryKeywords: ['mineral based soil', 'best citrus soil', 'root rot prevention soil'],
  },
];

const stickyHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  padding: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
  background: '#f9fafb',
  color: '#6b7280',
  fontSize: '0.75rem',
  fontWeight: 600,
  textAlign: 'left',
  letterSpacing: '0.03em',
};

const cellStyle: React.CSSProperties = {
  padding: '0.75rem',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'top',
};

const buttonStyle = (disabled = false, background = '#fff', color = '#111827'): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.4rem 0.75rem',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  background: disabled ? '#e5e7eb' : background,
  color,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.65 : 1,
  fontSize: '0.8125rem',
  fontWeight: 600,
});

const statusStyle = (status: BestOfStatus): React.CSSProperties => {
  const colors: Record<BestOfStatus, { background: string; color: string }> = {
    pending: { background: '#f3f4f6', color: '#374151' },
    running: { background: '#dbeafe', color: '#1d4ed8' },
    completed: { background: '#dcfce7', color: '#166534' },
    error: { background: '#fee2e2', color: '#991b1b' },
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.25rem 0.55rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 700,
    background: colors[status].background,
    color: colors[status].color,
    textTransform: 'capitalize',
  };
};

const buildBestOfTitle = (itemName: string, location: string): string =>
  location ? `Best Place to Buy ${itemName} in ${location}` : `Best Place to Buy ${itemName}`;

const normalizeCsvInput = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeUrlsInput = (value: string): string[] =>
  value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const extractWorkflowDetailsFromResult = (result: any): BestOfRow['workflowDetails'] => {
  if (!result?.workflow_data) return undefined;

  const { workflow_data } = result;
  const workflowDetails: BestOfRow['workflowDetails'] = { currentStage: 'completed' };

  if (workflow_data.firecrawl_analysis) {
    const firecrawl = workflow_data.firecrawl_analysis;
    workflowDetails.firecrawl = {
      urls_analyzed: firecrawl.top_results?.map((row: any) => row.url) || [],
      competitor_titles: firecrawl.top_results?.map((row: any) => row.title) || [],
      key_topics: firecrawl.top_results?.flatMap((row: any) => row.key_topics || []) || [],
      content_structure_insights: firecrawl.top_results?.map((row: any) => `${row.word_count || 0} words`) || [],
      search_metadata: firecrawl.search_metadata,
    };
  }

  const deepResearch = workflow_data.web_research || workflow_data.deep_research;
  if (deepResearch) {
    workflowDetails.deepResearch = {
      key_insights: deepResearch.research_findings?.key_insights || [],
      market_trends: deepResearch.research_findings?.market_trends || [],
      audience_needs: deepResearch.research_findings?.audience_needs || [],
      content_gaps: deepResearch.research_findings?.content_gaps || [],
      research_confidence: deepResearch.research_depth?.confidence_score || 0,
      sources_analyzed: deepResearch.research_depth?.sources_analyzed || 0,
      model_used: deepResearch.research_metadata?.model_used || 'Unknown',
    };
  }

  if (workflow_data.gap_analysis) {
    const gapAnalysis = workflow_data.gap_analysis;
    workflowDetails.gapAnalysis = {
      identified_gaps: gapAnalysis.analysis_summary?.identified_gaps || [],
      competitive_coverage: gapAnalysis.analysis_summary?.competitive_coverage || '',
      recommended_angle: gapAnalysis.analysis_summary?.recommended_content_angle || '',
      gap_scores: gapAnalysis.gap_score || {},
      seo_suggestions: gapAnalysis.content_strategy?.seo_optimization_suggestions || [],
    };
  }

  if (result.generation_metadata) {
    workflowDetails.contentGeneration = {
      processing_steps: result.generation_metadata.processing_steps || [],
      brand_variables_processed: result.metadata?.brand_variables_processed || 0,
      citations_count: result.metadata?.citations_included ? 1 : 0,
      quality_score: result.generation_metadata.content_quality_score || 0,
      model_pipeline: result.generation_metadata.model_pipeline || [],
    };
  }

  return workflowDetails;
};

/**
 * Creates the default batch rows for each approved brand.
 * Why this matters: USCN needs every citrus tree ready for batch generation, while
 * Dr. Mani's Magic starts with product-system rows that users can extend manually.
 */
const buildDefaultRows = (brandKey: BestOfBrandKey, location: string): BestOfRow[] => {
  if (brandKey === 'uscitrusnursery') {
    return CITRUS_TREES.map((tree) => {
      const title = buildBestOfTitle(tree.treeName, location);
      return {
        id: `uscn-${tree.id}`,
        brandKey,
        keyword: title,
        itemName: tree.treeName,
        location,
        articleTitle: title,
        primaryKeyword: title,
        secondaryKeywords: [tree.treeName, `${tree.treeName} for sale`, `buy ${tree.treeName}`],
        competitorUrls: [],
        contentLength: 'long',
        category: tree.category,
        status: 'pending',
        progress: '',
        output: '',
        createdAt: new Date(),
      };
    });
  }

  return DR_MANI_DEFAULT_ROWS.map((item) => {
    const title = buildBestOfTitle(item.itemName, '');
    return {
      id: `dmm-${item.id}`,
      brandKey,
      keyword: item.primaryKeyword,
      itemName: item.itemName,
      location: '',
      articleTitle: title,
      primaryKeyword: item.primaryKeyword,
      secondaryKeywords: item.secondaryKeywords,
      competitorUrls: [],
      contentLength: 'long',
      category: item.category,
      status: 'pending',
      progress: '',
      output: '',
      createdAt: new Date(),
    };
  });
};

const BestOfArticlesAgentPage: React.FC = () => {
  const { brand, brandKey } = useBrand();
  const activeBrandKey: BestOfBrandKey = brandKey === 'drmanismagic' ? 'drmanismagic' : 'uscitrusnursery';
  const storageKey = getBrandStorageKey(activeBrandKey, 'best_of_articles_progress');

  const [rows, setRows] = useState<BestOfRow[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'category' | 'itemName' | 'status' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [isSequentialRunning, setIsSequentialRunning] = useState(false);
  const [sequentialRemaining, setSequentialRemaining] = useState<string[]>([]);
  const [defaultLocation, setDefaultLocation] = useState(DEFAULT_LOCATION);
  const [customItemName, setCustomItemName] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [customCompetitorUrls, setCustomCompetitorUrls] = useState('');
  const [showContentActionModal, setShowContentActionModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedOutputRow, setSelectedOutputRow] = useState<BestOfRow | null>(null);
  const [popupState, setPopupState] = useState<{ isVisible: boolean; rowId: string | null; position: { x: number; y: number } }>({
    isVisible: false,
    rowId: null,
    position: { x: 0, y: 0 },
  });
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

  const rowsRef = useRef<BestOfRow[]>([]);
  const stopRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
      abortRef.current?.abort();
    };
  }, [hideTimeout]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.rows)) {
          setRows(
            parsed.rows.map((row: any) => ({
              ...row,
              createdAt: row.createdAt ? new Date(row.createdAt) : new Date(),
              status: row.status === 'running' ? 'error' : row.status,
              progress: row.status === 'running' ? 'Interrupted by page reload' : row.progress,
            }))
          );
        } else {
          setRows(buildDefaultRows(activeBrandKey, parsed.defaultLocation || DEFAULT_LOCATION));
        }

        if (Array.isArray(parsed.selectedRows)) {
          setSelectedRows(new Set(parsed.selectedRows));
        }
        if (parsed.defaultLocation) {
          setDefaultLocation(parsed.defaultLocation);
        }
        return;
      }
    } catch {
      setRows(buildDefaultRows(activeBrandKey, DEFAULT_LOCATION));
      return;
    }

    setRows(buildDefaultRows(activeBrandKey, DEFAULT_LOCATION));
  }, [activeBrandKey, storageKey]);

  useEffect(() => {
    if (rows.length === 0) {
      return;
    }

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        rows,
        selectedRows: Array.from(selectedRows),
        defaultLocation,
      })
    );
  }, [defaultLocation, rows, selectedRows, storageKey]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return rows;
    }

    return rows.filter((row) =>
      [row.itemName, row.articleTitle, row.primaryKeyword, row.category, row.location]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [rows, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortField) {
      return filteredRows;
    }

    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const left = String(a[sortField] || '');
      const right = String(b[sortField] || '');
      if (left === right) return 0;
      const base = left < right ? -1 : 1;
      return sortDirection === 'asc' ? base : -base;
    });
    return copy;
  }, [filteredRows, sortDirection, sortField]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);
  const anyRunning = rows.some((row) => row.status === 'running');

  useEffect(() => {
    setPage((value) => Math.min(value, Math.max(1, Math.ceil(sortedRows.length / pageSize))));
  }, [pageSize, sortedRows.length]);

  const updateRow = useCallback((rowId: string, updates: Partial<BestOfRow>) => {
    setRows((currentRows) => currentRows.map((row) => (row.id === rowId ? { ...row, ...updates } : row)));
  }, []);

  const loadBrandKit = useCallback(() => {
    try {
      const stored = getBrandStorageItem(activeBrandKey, 'brand_kit', {
        legacyKeys: ['us_citrus_brand_kit', 'us_citrus_ai_brand_kit'],
      });
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, [activeBrandKey]);

  const loadSitemapData = useCallback((): Array<{ title: string; description: string; url: string }> | null => {
    try {
      const stored = getBrandStorageItem(activeBrandKey, 'sitemap_data', {
        legacyKeys: ['us_citrus_sitemap_data', 'us_citrus_ai_sitemap_data'],
      });
      if (!stored) {
        return null;
      }

      const sitemaps = JSON.parse(stored);
      return sitemaps
        .flatMap((sitemap: any) =>
          (sitemap.urls || []).map((url: any) => ({
            title: url.title,
            description: url.description,
            url: url.url,
          }))
        )
        .slice(0, 30);
    } catch {
      return null;
    }
  }, [activeBrandKey]);

  /**
   * Polls a Best Of generation job until it completes or fails.
   * Why this matters: users need row-level progress for long-running SERP research
   * instead of a frozen page with no clue what the agent is doing.
   */
  const pollJob = useCallback(
    async (rowId: string, jobId: string, signal?: AbortSignal) => {
      const maxPolls = 180;
      let polls = 0;

      const poll = async () => {
        if (signal?.aborted) {
          updateRow(rowId, { status: 'error', progress: 'Cancelled by user' });
          return;
        }

        if (polls++ >= maxPolls) {
          updateRow(rowId, { status: 'error', progress: 'Timed out after 15 minutes' });
          return;
        }

        try {
          const response = await fetch(API_ENDPOINTS.bestOfArticlesJobStatus(jobId), {
            headers: { 'x-api-key': process.env.REACT_APP_INTERNAL_API_KEY || '' },
            ...(signal ? { signal } : {}),
          });
          const payload = await response.json();

          if (!payload.success) {
            updateRow(rowId, { status: 'error', progress: payload.error || 'Unknown error' });
            return;
          }

          const { data } = payload;
          updateRow(rowId, { progress: data.message || `Stage: ${data.stage}` });

          if (data.status === 'completed' && data.result) {
            const result = data.result;
            const workflowDetails = extractWorkflowDetailsFromResult(result);
            updateRow(rowId, {
              status: 'completed',
              progress: 'Complete!',
              output: result.content || result.raw_content || '',
              ...(result.metadata ? { metadata: result.metadata } : {}),
              generationResult: result,
              ...(workflowDetails ? { workflowDetails } : {}),
            });
            return;
          }

          if (data.status === 'error') {
            updateRow(rowId, { status: 'error', progress: data.error || 'Generation failed' });
            return;
          }

          setTimeout(poll, 5000);
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            updateRow(rowId, { status: 'error', progress: 'Cancelled by user' });
            return;
          }

          updateRow(rowId, {
            status: 'error',
            progress: error instanceof Error ? error.message : 'Polling failed',
          });
        }
      };

      poll();
    },
    [updateRow]
  );

  /**
   * Starts generation for one table row.
   * Why this matters: each row carries its own tree or product, keyword, location,
   * and competitor URL hints, so the backend can produce the exact listicle requested.
   */
  const executeRow = useCallback(
    async (rowId: string) => {
      const row = rowsRef.current.find((item) => item.id === rowId);
      if (!row) return;

      const controller = new AbortController();
      abortRef.current = controller;
      updateRow(rowId, { status: 'running', progress: 'Starting Best Of agent...', output: '' });

      try {
        const topicRowIndex = rowsRef.current.findIndex((item) => item.id === rowId);
        const authorKey = topicRowIndex >= 0 && topicRowIndex % 2 === 0 ? 'dr_mani' : 'ron_skaria';
        const response = await fetch(API_ENDPOINTS.bestOfArticlesGenerateAsync, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_INTERNAL_API_KEY || '',
          },
          body: JSON.stringify({
            brandKey: row.brandKey,
            itemName: row.itemName,
            location: row.location,
            articleTitle: row.articleTitle,
            primaryKeyword: row.primaryKeyword,
            secondaryKeywords: row.secondaryKeywords,
            competitorUrls: row.competitorUrls,
            content_length: row.contentLength,
            audience:
              row.brandKey === 'uscitrusnursery'
                ? 'home citrus growers searching for the best place to buy a citrus tree'
                : 'home growers comparing soil, fertilizer, and microbial products',
            brand_kit: loadBrandKit(),
            sitemap_data: loadSitemapData(),
            ...(row.brandKey === 'drmanismagic'
              ? {
                  authorKey,
                  topicRowIndex: topicRowIndex >= 0 ? topicRowIndex : 0,
                }
              : {}),
          }),
          signal: controller.signal,
        });
        const payload = await response.json();

        if (!payload.success || !payload.jobId) {
          updateRow(rowId, { status: 'error', progress: payload.error || 'Failed to start' });
          return;
        }

        updateRow(rowId, { progress: 'Job queued...' });
        await pollJob(rowId, payload.jobId, controller.signal);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          updateRow(rowId, { status: 'error', progress: 'Cancelled by user' });
          return;
        }

        updateRow(rowId, {
          status: 'error',
          progress: error instanceof Error ? error.message : 'Network error',
        });
      }
    },
    [loadBrandKit, loadSitemapData, pollJob, updateRow]
  );

  const executeSelected = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;

      stopRef.current = false;
      setIsSequentialRunning(true);
      setSequentialRemaining([...ids]);

      for (const id of ids) {
        if (stopRef.current) break;
        setSequentialRemaining((remaining) => remaining.filter((rowId) => rowId !== id));
        await executeRow(id);
      }

      setIsSequentialRunning(false);
    },
    [executeRow]
  );

  const toggleSort = (field: 'category' | 'itemName' | 'status') => {
    setSortField(field);
    setSortDirection((currentDirection) => (sortField === field && currentDirection === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const toggleRowSelection = (rowId: string) => {
    setSelectedRows((currentSelection) => {
      const next = new Set(currentSelection);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const selectAllFiltered = () => setSelectedRows(new Set(filteredRows.map((row) => row.id)));
  const clearSelection = () => setSelectedRows(new Set());

  /**
   * Applies a new location to pending USCN rows and rebuilds their titles.
   * Why this matters: location is part of the search intent, so a Texas article
   * and a Florida article should not accidentally share the same keyword.
   */
  const applyLocationToPendingRows = () => {
    const nextLocation = defaultLocation.trim();
    setRows((currentRows) =>
      currentRows.map((row) => {
        // Allow applying location to pending rows of the currently active brand
        if (row.brandKey !== activeBrandKey || row.status !== 'pending') {
          return row;
        }

        const nextTitle = buildBestOfTitle(row.itemName, nextLocation);
        return {
          ...row,
          location: nextLocation,
          articleTitle: nextTitle,
          primaryKeyword: nextTitle,
          keyword: nextTitle,
        };
      })
    );
  };

  const addCustomRow = () => {
    const itemName = customItemName.trim();
    if (!itemName) return;

    const location = customLocation.trim();
    const title = buildBestOfTitle(itemName, location);
    const now = Date.now();

    setRows((currentRows) => [
      {
        id: `custom-${activeBrandKey}-${now}`,
        brandKey: activeBrandKey,
        keyword: title,
        itemName,
        location,
        articleTitle: title,
        primaryKeyword: title,
        secondaryKeywords: normalizeCsvInput(itemName),
        competitorUrls: normalizeUrlsInput(customCompetitorUrls),
        contentLength: 'long',
        category: activeBrandKey === 'uscitrusnursery' ? 'Custom Tree' : 'Custom Product',
        status: 'pending',
        progress: '',
        output: '',
        createdAt: new Date(),
      },
      ...currentRows,
    ]);

    setCustomItemName('');
    setCustomLocation('');
    setCustomCompetitorUrls('');
  };

  const resetRows = () => {
    const nextRows = buildDefaultRows(activeBrandKey, defaultLocation.trim() || DEFAULT_LOCATION);
    setRows(nextRows);
    setSelectedRows(new Set());
    setPage(1);
  };

  /**
   * Shows the confirmation modal before deleting selected rows.
   * Why this matters: Prevents accidental data loss by prompting the user to confirm.
   */
  const showBulkDeleteConfirmation = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  /**
   * Confirms the deletion of selected rows, updates state, and closes the modal.
   * Why this matters: Performs the actual destructive action once user intent has been verified.
   */
  const confirmBulkDelete = () => {
    const selectedIds = new Set(selectedRows);
    setRows((currentRows) => currentRows.filter((row) => !selectedIds.has(row.id)));
    setSelectedRows(new Set());
    setShowBulkDeleteModal(false);
  };

  /**
   * Cancels the deletion and closes the confirmation modal.
   * Why this matters: Safe fallback to let users exit the modal without changing anything.
   */
  const cancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  const openOutputModal = (row: BestOfRow) => {
    setSelectedOutputRow(row);
    setShowContentActionModal(true);
  };

  const showWorkflowPopup = (rowId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }

    setPopupState({
      isVisible: true,
      rowId,
      position: { x: event.clientX, y: event.clientY },
    });
  };

  const completedCount = rows.filter((row) => row.status === 'completed').length;
  const runningCount = rows.filter((row) => row.status === 'running').length;

  return (
    <div style={{ padding: '2rem', maxWidth: '100%', overflow: 'hidden' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#111827', margin: 0 }}>Best Of Articles Agent</h1>
        <p style={{ margin: '0.5rem 0 0', color: '#6b7280', maxWidth: '56rem', fontSize: '0.875rem' }}>
          {activeBrandKey === 'uscitrusnursery' 
            ? 'Generate ranked listicle articles for US Citrus Nursery. Rows come from the citrus tree catalog and can be extended with custom rows.'
            : "Generate ranked listicle articles for Dr. Mani's Magic. Rows start with the core product system and can be extended with custom rows."
          }
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ padding: '1rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.75rem' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Rows</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{rows.length}</div>
        </div>
        <div style={{ padding: '1rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.75rem' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Completed</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#166534' }}>{completedCount}</div>
        </div>
        <div style={{ padding: '1rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.75rem' }}>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Running</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1d4ed8' }}>{runningCount}</div>
        </div>
      </div>

      <div style={{ padding: '1rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#374151', marginBottom: '0.35rem' }}>
              Default location for pending rows
            </label>
            <input
              value={defaultLocation}
              onChange={(event) => setDefaultLocation(event.target.value)}
              style={{ padding: '0.55rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', minWidth: '16rem' }}
            />
          </div>

          <button type="button" onClick={applyLocationToPendingRows} style={buttonStyle(false, '#111827', '#fff')}>
            Apply Location
          </button>

          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#374151', marginBottom: '0.35rem' }}>
              Add custom item
            </label>
            <input
              placeholder={activeBrandKey === 'uscitrusnursery' ? 'Example: Rio Red Grapefruit Tree' : 'Example: organic citrus fertilizer'}
              value={customItemName}
              onChange={(event) => setCustomItemName(event.target.value)}
              style={{ padding: '0.55rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', minWidth: '18rem' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#374151', marginBottom: '0.35rem' }}>
              Location
            </label>
            <input
              placeholder="Optional"
              value={customLocation}
              onChange={(event) => setCustomLocation(event.target.value)}
              style={{ padding: '0.55rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', minWidth: '10rem' }}
            />
          </div>

          <div style={{ flex: '1 1 18rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#374151', marginBottom: '0.35rem' }}>
              Competitor URLs
            </label>
            <input
              placeholder="Optional, comma-separated"
              value={customCompetitorUrls}
              onChange={(event) => setCustomCompetitorUrls(event.target.value)}
              style={{ padding: '0.55rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', width: '100%' }}
            />
          </div>

          <button type="button" onClick={addCustomRow} disabled={!customItemName.trim()} style={buttonStyle(!customItemName.trim(), '#EBF212', '#000')}>
            Add Row
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: '440px' }}>
          <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search keywords, tree names, products, or locations..."
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(1);
            }}
            style={{ width: '100%', padding: '0.5rem 2rem 0.5rem 2.25rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af' }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <button type="button" onClick={() => (selectedRows.size > 0 ? clearSelection() : selectAllFiltered())} style={buttonStyle(false)}>
          {selectedRows.size > 0 ? `Unselect (${selectedRows.size})` : 'Select All'}
        </button>

        {selectedRows.size > 0 && (
          <button
            type="button"
            onClick={showBulkDeleteConfirmation}
            style={buttonStyle(false, '#fff', '#dc2626')}
          >
            Delete Selected ({selectedRows.size})
          </button>
        )}

        <button
          type="button"
          onClick={() => void executeSelected(Array.from(selectedRows))}
          disabled={selectedRows.size === 0 || isSequentialRunning}
          style={buttonStyle(selectedRows.size === 0 || isSequentialRunning, '#111827', '#fff')}
        >
          <Play size={14} />
          Run Selected ({selectedRows.size})
        </button>

        {isSequentialRunning && (
          <button
            type="button"
            onClick={() => {
              stopRef.current = true;
              abortRef.current?.abort();
            }}
            style={buttonStyle(false, '#dc2626', '#fff')}
          >
            Stop
          </button>
        )}

        {!isSequentialRunning && sequentialRemaining.length > 0 && (
          <button type="button" onClick={() => void executeSelected(sequentialRemaining)} style={buttonStyle(false, '#166534', '#fff')}>
            Resume ({sequentialRemaining.length})
          </button>
        )}

        <button type="button" onClick={resetRows} disabled={anyRunning} style={buttonStyle(anyRunning)}>
          Reset Rows
        </button>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.75rem', overflow: 'hidden' }}>
        <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
          <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
            <colgroup>
              <col style={{ width: '3%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '34%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={stickyHeaderStyle}>
                  <input
                    type="checkbox"
                    checked={filteredRows.length > 0 && selectedRows.size === filteredRows.length}
                    onChange={() => (selectedRows.size === filteredRows.length ? clearSelection() : selectAllFiltered())}
                  />
                </th>
                <th style={stickyHeaderStyle}>
                  <button type="button" onClick={() => toggleSort('itemName')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 600, color: 'inherit', padding: 0 }}>
                    TREE / PRODUCT
                  </button>
                </th>
                <th style={stickyHeaderStyle}>GENERATED TITLE / QUERY</th>
                <th style={stickyHeaderStyle}>
                  <button type="button" onClick={() => toggleSort('category')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 600, color: 'inherit', padding: 0 }}>
                    CATEGORY
                  </button>
                </th>
                <th style={stickyHeaderStyle}>
                  <button type="button" onClick={() => toggleSort('status')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 600, color: 'inherit', padding: 0 }}>
                    STATUS
                  </button>
                </th>
                <th style={stickyHeaderStyle}>EXECUTE</th>
                <th style={stickyHeaderStyle}>DETAILS</th>
                <th style={stickyHeaderStyle}>ACTIONS</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: '0.875rem' }}>
              {paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '3rem 1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                    {searchTerm ? 'No Best Of rows match your search' : 'No Best Of rows available'}
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row) => (
                  <tr key={row.id} style={{ background: row.status === 'completed' ? '#f0fdf4' : '#fff' }}>
                    <td style={cellStyle}>
                      <input type="checkbox" checked={selectedRows.has(row.id)} onChange={() => toggleRowSelection(row.id)} />
                    </td>
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {/* Green tree icon to match the look-and-feel of other blog creators */}
                        <TreePine size={16} style={{ color: '#22c55e', flexShrink: 0 }} />
                        <div>
                          {/* Changed from font-weight 700 to 500 to make the text look softer and less harsh */}
                          <div style={{ fontWeight: 500, color: '#111827' }}>{row.itemName}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{row.location || 'No location'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={cellStyle}>
                      {/* Changed from font-weight 600 to 500 to match standard blog creator styles and reduce visual weight */}
                      <div style={{ color: '#111827', fontWeight: 500 }}>{row.articleTitle}</div>
                      {/* Subheadline ({row.primaryKeyword}) was removed to keep the table clean and less cluttered */}
                      {row.progress && row.status !== 'pending' && (
                        <div style={{ fontSize: '0.75rem', color: row.status === 'error' ? '#dc2626' : '#6b7280', marginTop: '0.25rem' }}>
                          {row.progress}
                        </div>
                      )}
                    </td>
                    <td style={cellStyle}>{row.category}</td>
                    <td style={cellStyle}>
                      <span style={statusStyle(row.status)}>
                        {row.status === 'completed' && <CheckCircle size={13} />}
                        {row.status === 'error' && <AlertTriangle size={13} />}
                        {row.status}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <button
                        type="button"
                        onClick={() => void executeRow(row.id)}
                        disabled={anyRunning}
                        style={buttonStyle(anyRunning, row.status === 'error' ? '#fee2e2' : '#fff', row.status === 'error' ? '#991b1b' : '#111827')}
                      >
                        {row.status === 'running' ? <RefreshCw size={14} /> : <Play size={14} />}
                        {row.status === 'completed' ? 'Re-run' : row.status === 'error' ? 'Retry' : row.status === 'running' ? 'Running' : 'Run'}
                      </button>
                    </td>
                    <td style={cellStyle}>
                      <button
                        type="button"
                        onClick={(event) => showWorkflowPopup(row.id, event)}
                        disabled={!row.workflowDetails}
                        style={buttonStyle(!row.workflowDetails)}
                      >
                        Show
                      </button>
                    </td>
                    <td style={cellStyle}>
                      <button
                        type="button"
                        onClick={() => openOutputModal(row)}
                        disabled={!row.output || row.status === 'running'}
                        style={buttonStyle(!row.output || row.status === 'running', '#EBF212', '#000')}
                      >
                        See Output
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#6b7280', fontSize: '0.875rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            Page {page} of {totalPages}. Showing {paginatedRows.length} of {filteredRows.length} filtered rows.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button type="button" onClick={() => setPage(1)} disabled={page === 1} style={buttonStyle(page === 1)}>
              {'<<'}
            </button>
            <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page === 1} style={buttonStyle(page === 1)}>
              {'<'}
            </button>
            <button type="button" onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={page === totalPages} style={buttonStyle(page === totalPages)}>
              {'>'}
            </button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={page === totalPages} style={buttonStyle(page === totalPages)}>
              {'>>'}
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              style={{ padding: '0.45rem 0.6rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: '#fff' }}
            >
              {[50, 100, 200, 500].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {popupState.isVisible &&
        popupState.rowId &&
        (() => {
          const row = rows.find((item) => item.id === popupState.rowId);
          if (!row?.workflowDetails) return null;

          return (
            <BlogCreatorWorkflowDetails
              workflowDetails={row.workflowDetails}
              status={row.status}
              isVisible={popupState.isVisible}
              keyword={row.primaryKeyword}
              position={popupState.position}
              generatedContent={row.output}
              onSeeOutput={() => openOutputModal(row)}
              onMobileClose={() => setPopupState((current) => ({ ...current, isVisible: false }))}
              onMouseEnter={() => {
                if (hideTimeout) {
                  clearTimeout(hideTimeout);
                }
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => setPopupState((current) => ({ ...current, isVisible: false })), 300);
                setHideTimeout(timeout);
              }}
            />
          );
        })()}

      {showContentActionModal && selectedOutputRow && (
        <BlogContentActionModal
          isOpen={showContentActionModal}
          onClose={() => {
            setShowContentActionModal(false);
            setSelectedOutputRow(null);
          }}
          keywordRow={selectedOutputRow}
          onContentUpdate={(rowId, newContent) => updateRow(rowId, { output: newContent })}
          onStatusUpdate={(rowId, status) => updateRow(rowId, { status })}
        />
      )}

      {/* Bulk Delete Confirmation Modal overlay */}
      {showBulkDeleteModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={cancelBulkDelete}
        >
          <div 
            style={{ 
              maxWidth: '500px', 
              width: '90%',
              position: 'relative',
              backgroundColor: '#ffffff',
              borderRadius: '0.75rem',
              border: '1px solid #e5e7eb',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '1.5rem' }}>
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ 
                  width: '3rem',
                  height: '3rem',
                  borderRadius: '50%',
                  backgroundColor: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <AlertTriangle style={{ width: '1.5rem', height: '1.5rem', color: '#dc2626' }} />
                </div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: '0 0 0.5rem', color: '#1f2937' }}>
                  Delete Selected Keywords?
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                  This action will permanently delete {selectedRows.size} selected row{selectedRows.size > 1 ? 's' : ''}. This cannot be undone.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={cancelBulkDelete}
                  style={buttonStyle(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmBulkDelete}
                  style={buttonStyle(false, '#dc2626', '#ffffff')}
                >
                  Delete Keywords
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BestOfArticlesAgentPage;
